import React, { useMemo } from 'react';
import { useCategoryContext } from './context/useCategoryContext';
import { useFilter } from '@srcDS/tastic/pages/category/hooks/useFilter';
import { isEmptyArray, isNil } from '@srcDS/utils/assertions';
import { useMedia } from 'react-use';
import BackToTop from '../../atoms/backToTop';
import Breadcrumbs from '../../molecules/breadcrumbs/Breadcrumbs';
import CategoryTitleAndDescription from '../../molecules/categoryTitleAndDescription';
import CategoryContent from '@srcDS/components/templates/category/CategoryContent';
import { EmptyCategory } from './EmptyCategory';
import SearchNoResults from '@srcDS/components/templates/search/SearchNoResults';
import Portal from '../../atoms/portal';
import PrimaryLoader from '../../atoms/primaryLoader/PrimaryLoader';
import FeaturedFilterList from '../../molecules/featuredFilterList';
import SeoTextArea, { SeoTextAreaProps } from '../../molecules/seoTextArea/SeoTextArea';

import { useEmptyResultCheck } from '@srcDS/components/templates/category/utils/useEmptyResultCheck';
import { useCategoryMetadata } from '@srcDS/components/templates/category/utils/useCategoryMetadata';

import styles from './Category.module.scss';

const Category: React.FC = () => {
  const {
    viewMode,
    setViewMode,
    data,
    searchPhrase,
    stream,
    mergedFacets,
    isGlobalLoaderShow,
    getListName,
    triggerRoute,
    isSearchPage,
    isSSR,
    currency,
    currentPage,
    breadcrumbs,
  } = useCategoryContext() as any;
  const { filterDisplayValues, setFilterDisplayValues } = useFilter();
  const { emptyResult } = useEmptyResultCheck(data, triggerRoute);
  const isMobile = useMedia('(max-width: 767px)', false);

  const {
    categoryTitle,
    categoryText,
    popularCategories,
    nostoPlacementId,
    stream: { projectSpecificData, ...rest },
  } = data;

  const { getTitleAndSubtitle, getFeaturedCategories, getSeoMetadata } = useCategoryMetadata(data, stream, currentPage);

  const pageTitle = useMemo(() => {
    return categoryTitle || categoryText ? (
      <div className={styles.pageTitle}>
        <CategoryTitleAndDescription title={categoryTitle} subtitle={categoryText} searchPhrase={searchPhrase} />
      </div>
    ) : null;
  }, [getTitleAndSubtitle, searchPhrase, categoryTitle, categoryText]);

  const featuredCategories = useMemo(() => {
    const featuredList = getFeaturedCategories();
    return featuredList ? (
      <div className={styles.featuredCategories}>
        <FeaturedFilterList list={featuredList} />
      </div>
    ) : null;
  }, [getFeaturedCategories]);

  const renderBreadcrumbs = useMemo(
    () => <Breadcrumbs className={styles.breadcrumbs} crumbs={breadcrumbs} />,
    [breadcrumbs],
  );

  if (isSearchPage && emptyResult || isSearchPage && !rest?.items?.length) {
    const { title } = getTitleAndSubtitle();
    return (
      <SearchNoResults
        title={title}
        searchPhrase={searchPhrase}
        nostoPlacementId={nostoPlacementId}
        currency={currency}
        isSSR={isSSR}
      />
    );
  }

  if (emptyResult) {
    return <EmptyCategory popularCategories={popularCategories} />;
  }

  const states = {
    viewMode,
    setViewMode,
    hasProducts: !!rest?.total || !!rest?.items?.length,
    hasFilters: !!mergedFacets?.length,
    numbOfTotalItems: stream?.total ?? 0,
  };

  return (
    <div className={styles.categoryRoot}>
      {pageTitle}
      {featuredCategories}
      <CategoryContent
        filterDisplayValues={filterDisplayValues}
        setFilterDisplayValues={setFilterDisplayValues}
        categoryTitle={categoryTitle}
        states={states}
        listName={getListName()}
        isMobile={isMobile}
        filtrableResult={
          (!isNil(stream?.query?.filter) && !isEmptyArray(stream?.query?.filter) && !stream?.items?.length) ||
          !stream?.items?.length
        }
        currentPage={currentPage}
      />
      {renderBreadcrumbs}
      <SeoTextArea {...(getSeoMetadata() as SeoTextAreaProps)} />

      {isGlobalLoaderShow && (
        <Portal renderInto={undefined}>
          <PrimaryLoader mode="fixed" />
        </Portal>
      )}
      <BackToTop />
    </div>
  );
};

export default React.memo(Category);
